import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { fetchPublicSiteContent } from '../supabaseClient';
import { PUBLIC_SITE_DEFAULTS, buildPublicSiteContentMap, normalizeBrandingNavItems } from '../publicSiteContent';
import './PublicSite.css';

// Rich-text fields (lead, proof text, solution descriptions) are stored as admin-authored
// HTML. Sanitize before rendering — only a handful of formatting tags are ever produced by
// the editor's toolbar, so the allowlist stays tight.
const richText = (html) => ({
  __html: DOMPurify.sanitize(html || '', { ALLOWED_TAGS: ['b', 'strong', 'i', 'em', 'u', 'ul', 'ol', 'li', 'br', 'span', 'blockquote'], ALLOWED_ATTR: ['style'] }),
});

const getPageKeyFromPath = (path = '') => String(path).replace(/^\//, '').trim();

const targetClass = (baseClassName, active) => [baseClassName, active ? 'public-psm-highlight' : ''].filter(Boolean).join(' ');

const isTargetActive = (highlightTarget, target) => highlightTarget === target;

const ServsoloLogo = ({ branding, highlightTarget, onClick, storeName }) => (
  <Link
    to="/"
    className="public-site-brand"
    aria-label={storeName || 'Servsolo home'}
    onClick={onClick ? (e) => { e.preventDefault(); onClick(); } : undefined}
    style={{
      '--public-logo-width': `${Number(branding?.logoWidthDesktop) || 280}px`,
      '--public-logo-width-mobile': `${Number(branding?.logoWidthMobile) || 210}px`,
    }}
  >
    <span
      className={targetClass('public-site-brand-mark', isTargetActive(highlightTarget, 'branding.logoImageUrl'))}
      aria-hidden="true"
      data-psm-target="branding.logoImageUrl"
    >
      {branding?.logoImageUrl ? (
        <img src={branding.logoImageUrl} alt={branding.logoAlt || storeName || 'logo'} className="public-site-brand-image" />
      ) : storeName ? (
        <span className="public-site-brand-name-text">{storeName}</span>
      ) : (
      <svg viewBox="0 0 760 220" role="presentation" focusable="false">
        <defs>
          <linearGradient id="servsoloBlue" x1="0%" x2="0%" y1="0%" y2="100%">
            <stop offset="0%" stopColor="#1ea0ff" />
            <stop offset="100%" stopColor="#124acb" />
          </linearGradient>
          <linearGradient id="servsoloOrange" x1="0%" x2="100%" y1="0%" y2="100%">
            <stop offset="0%" stopColor="#ff8c14" />
            <stop offset="100%" stopColor="#ff4b00" />
          </linearGradient>
        </defs>
        <path d="M22 168c28 18 73 23 118 12 30-8 58-24 79-49l-12 31c-31 25-69 38-112 40-31 2-59-4-84-18l11-16Z" fill="url(#servsoloBlue)" />
        <path d="M145 176c42-1 76-12 102-31 30-22 48-55 53-101l18 20c-8 51-31 90-70 118-31 22-67 34-110 36-36 1-67-6-94-21l24-21c24 1 49 1 77 0Z" fill="url(#servsoloOrange)" />
        <circle cx="92" cy="61" r="22" fill="url(#servsoloBlue)" />
        <path d="M70 92c9-9 18-13 28-13 10 0 20 4 29 13 8 8 12 17 12 28v50H112v-52c0-6-2-10-6-14-4-4-9-6-14-6-6 0-10 2-14 6-4 4-6 9-6 14v52H45v-50c0-11 4-20 12-28Z" fill="url(#servsoloBlue)" />
        <path d="M146 77h46c7 0 11 4 11 11v68c0 7-4 11-11 11h-46c-7 0-11-4-11-11V88c0-7 4-11 11-11Z" fill="url(#servsoloOrange)" />
        <path d="M151 84h36c5 0 8 3 8 8v45c0 5-3 8-8 8h-36c-5 0-8-3-8-8V92c0-5 3-8 8-8Z" fill="#ffffff" />
        <rect x="154" y="90" width="30" height="17" rx="3" transform="rotate(8 154 90)" fill="url(#servsoloOrange)" opacity="0.9" />
        <rect x="154" y="115" width="26" height="5" rx="2.5" fill="url(#servsoloBlue)" opacity="0.86" />
        <rect x="154" y="124" width="26" height="5" rx="2.5" fill="url(#servsoloBlue)" opacity="0.86" />
        <rect x="154" y="133" width="15" height="5" rx="2.5" fill="url(#servsoloBlue)" opacity="0.86" />
        <rect x="157" y="148" width="25" height="9" rx="3" fill="#7a3d1a" opacity="0.9" />
        <path d="M132 107c8-1 15 1 20 6 4 4 6 9 6 15 0 6-2 11-6 15-5 5-12 7-20 6l-4-18 4-24Z" fill="url(#servsoloBlue)" />
        <g fill="url(#servsoloOrange)">
          <circle cx="243" cy="57" r="24" />
          <circle cx="277" cy="101" r="27" />
        </g>
        <g fill="#ffffff">
          <circle cx="243" cy="57" r="11" />
          <circle cx="277" cy="101" r="12" />
        </g>
        <g fill="url(#servsoloOrange)">
          <rect x="238" y="18" width="10" height="17" rx="3" />
          <rect x="238" y="79" width="10" height="17" rx="3" />
          <rect x="204" y="52" width="17" height="10" rx="3" />
          <rect x="265" y="52" width="17" height="10" rx="3" />
          <rect x="213" y="27" width="11" height="17" rx="3" transform="rotate(-42 213 27)" />
          <rect x="259" y="72" width="11" height="17" rx="3" transform="rotate(-42 259 72)" />
          <rect x="260" y="27" width="11" height="17" rx="3" transform="rotate(42 260 27)" />
          <rect x="214" y="72" width="11" height="17" rx="3" transform="rotate(42 214 72)" />
          <rect x="271" y="56" width="11" height="18" rx="3" />
          <rect x="271" y="127" width="11" height="18" rx="3" />
          <rect x="232" y="96" width="18" height="11" rx="3" />
          <rect x="304" y="96" width="18" height="11" rx="3" />
          <rect x="243" y="67" width="12" height="18" rx="3" transform="rotate(-42 243 67)" />
          <rect x="297" y="120" width="12" height="18" rx="3" transform="rotate(-42 297 120)" />
          <rect x="297" y="68" width="12" height="18" rx="3" transform="rotate(42 297 68)" />
          <rect x="243" y="120" width="12" height="18" rx="3" transform="rotate(42 243 120)" />
        </g>
        <text x="330" y="120" fontFamily="Manrope, sans-serif" fontSize="92" fontWeight="800" letterSpacing="-5">
          <tspan fill="url(#servsoloBlue)">Serv</tspan><tspan fill="url(#servsoloOrange)">Solo</tspan>
        </text>
        <text x="432" y="176" fontFamily="Manrope, sans-serif" fontSize="38" fontWeight="600" letterSpacing="0.5" fill="#667085">You run it all.</text>
      </svg>
      )}
    </span>
  </Link>
);

// Renders the Proof section's freeform extra blocks. "container" recurses into its own
// children — same function, same markup, just nested inside a wrapping div — which is why
// adding container support here needed no new rendering logic, only one extra case.
// Custom sections default to a visibly different background from the page's own gradient,
// so a newly-added section reads as its own distinct block on the page from the start.
const DEFAULT_CUSTOM_SECTION_BG = '#ffffff';

// branding.wrapperEnabled === false ("Add Wrapper?" off) means the header and every section
// card go fully transparent — no separate wrapper-colored layer at all, so they show
// whatever's behind them (the page background) directly, reading as one continuous full-width
// surface instead of a distinct header/card on top of the page. An explicit per-section
// background (cs.background) still applies either way — that's a deliberate per-section
// choice, not the global wrapper fallback this toggle controls.
const getSectionBackground = (cs, branding) => cs.background || (branding?.wrapperEnabled === false ? 'transparent' : (branding?.wrapperColor || DEFAULT_CUSTOM_SECTION_BG));

// Computes a container/group/section's actual border/background inline style — same logic
// as the editor's getContainerStyle. fallbackBorderStyle matters because Containers always
// had a 1px dashed border baked into their CSS class, but Groups and Sections never had a
// border at all — defaulting everything to 'dashed' would silently add one where there
// never was one before this feature existed.
const getContainerStyle = (block, fallbackBorderStyle = 'dashed') => {
  const color = /^#[0-9a-f]{6}$/i.test(block.borderColor || '') ? block.borderColor : '#9ca3af';
  const styleKey = block.borderStyle || fallbackBorderStyle;
  const style = { background: block.background || undefined };
  if (styleKey === 'none') {
    style.border = 'none';
  } else if (styleKey === 'bottom') {
    style.border = 'none';
    style.borderBottom = `1px solid ${color}`;
  } else if (styleKey === 'bottom-thick') {
    style.border = 'none';
    style.borderBottom = `3px solid ${color}`;
  } else if (styleKey === 'solid-thick') {
    style.border = `3px solid ${color}`;
  } else {
    style.border = `1px ${styleKey} ${color}`;
  }
  return style;
};

// Same border-style logic as getContainerStyle, but for Groups specifically: a
// full-perimeter border (solid/dotted/dashed/solid-thick) renders as outline instead of
// border. Outline never participates in box-model sizing (unlike border, which — combined
// with .public-proof-block-group's width:fit-content — added to the card's own real required
// width, throwing off the row/item width math that assumes a fixed, border-free amount of
// padding to subtract; that's what was wrapping a group's last item early as soon as any
// border was applied). 'bottom'/'bottom-thick' stay as a real border-bottom, unaffected
// either way since a bottom-only line was never consuming horizontal space to begin with.
const getGroupContainerStyle = (block) => {
  const color = /^#[0-9a-f]{6}$/i.test(block.borderColor || '') ? block.borderColor : '#9ca3af';
  const styleKey = block.borderStyle || 'none';
  const style = { background: block.background || undefined, border: 'none' };
  if (styleKey === 'bottom') {
    style.borderBottom = `1px solid ${color}`;
  } else if (styleKey === 'bottom-thick') {
    style.borderBottom = `3px solid ${color}`;
  } else if (styleKey === 'solid-thick') {
    style.outline = `3px solid ${color}`;
  } else if (styleKey !== 'none') {
    style.outline = `1px ${styleKey} ${color}`;
  }
  return style;
};

// One item inside a Group — same markup as the equivalent standalone block, minus any width
// class (the Group's own .public-group-item wrapper controls width via the chosen column
// count instead), and button/chip stretch to fill that wrapper (see .public-group-item
// .public-hero-secondary / .public-feature-pill in PublicSite.css) instead of staying
// intrinsically pill-sized like they do outside a group.
const renderGroupItemContent = (groupType, item, index) => {
  if (groupType === 'image') {
    return <img key={index} src={item.src} alt={item.alt || ''} className="public-proof-block-image" loading="lazy" />;
  }
  if (groupType === 'button') {
    return (
      <Link key={index} className="public-hero-secondary public-proof-block-button" to={item.to || '/'} style={{ background: item.bgColor || undefined }}>
        <span dangerouslySetInnerHTML={richText(item.label)} />
      </Link>
    );
  }
  if (groupType === 'chip') {
    const chipStyle = { background: item.bgColor || undefined };
    return item.to
      ? <Link key={index} className="public-feature-pill public-proof-block-button" to={item.to} style={chipStyle} dangerouslySetInnerHTML={richText(item.html)} />
      : <span key={index} className="public-feature-pill public-proof-block-button" style={chipStyle} dangerouslySetInnerHTML={richText(item.html)} />;
  }
  if (groupType === 'stat') {
    return (
      <div key={index} className="public-metric-card">
        <strong dangerouslySetInnerHTML={richText(item.value)} />
        <span dangerouslySetInnerHTML={richText(item.label)} />
      </div>
    );
  }
  // solution
  return (
    <article key={index} className="public-solution-card">
      <img src={item.image} alt={item.alt || ''} className="public-solution-image" loading="lazy" />
      <div className="public-solution-copy">
        <h3 dangerouslySetInnerHTML={richText(item.title)} />
        <p dangerouslySetInnerHTML={richText(item.text)} />
      </div>
    </article>
  );
};

const renderGroup = (block) => (
  <div className="public-group-row" style={{ '--psm-group-columns': block.columns || 4 }}>
    {(Array.isArray(block.children) ? block.children : []).map((item, index) => (
      <div key={index} className="public-group-item">
        {renderGroupItemContent(block.groupType, item, index)}
      </div>
    ))}
  </div>
);

const renderProofBlocks = (blocks) => (
  <div className="public-proof-blocks">
    {blocks.map((block, index) => {
      // Every item except "container" always renders at full width — matches the editor,
      // which no longer offers a per-item width choice now that the container an item lives
      // in already controls width.
      const widthClass = `public-proof-block-width-${block.type === 'container' ? (block.width || 'full') : 'large'}`;
      if (block.type === 'image') {
        return <img key={index} src={block.src} alt={block.alt || ''} className={`public-proof-block-image ${widthClass}`} loading="lazy" />;
      }
      if (block.type === 'button') {
        return (
          <Link key={index} className="public-hero-secondary public-proof-block-button" to={block.to || '/'} style={{ background: block.bgColor || undefined }}>
            <span dangerouslySetInnerHTML={richText(block.label)} />
          </Link>
        );
      }
      if (block.type === 'chip') {
        const chipStyle = { background: block.bgColor || undefined };
        return block.to
          ? <Link key={index} className="public-feature-pill public-proof-block-button" to={block.to} style={chipStyle} dangerouslySetInnerHTML={richText(block.html)} />
          : <span key={index} className="public-feature-pill public-proof-block-button" style={chipStyle} dangerouslySetInnerHTML={richText(block.html)} />;
      }
      if (block.type === 'divider') {
        return <hr key={index} className="public-proof-block-divider" />;
      }
      if (block.type === 'gap') {
        return <div key={index} className={`public-proof-block-gap public-proof-block-gap-${block.size || 'medium'}`} />;
      }
      if (block.type === 'stat') {
        return (
          <div key={index} className={`public-metric-card ${widthClass}`}>
            <strong dangerouslySetInnerHTML={richText(block.value)} />
            <span dangerouslySetInnerHTML={richText(block.label)} />
          </div>
        );
      }
      if (block.type === 'solution') {
        return (
          <article key={index} className={`public-solution-card ${widthClass}`}>
            <img src={block.image} alt={block.alt || ''} className="public-solution-image" loading="lazy" />
            <div className="public-solution-copy">
              <h3 dangerouslySetInnerHTML={richText(block.title)} />
              <p dangerouslySetInnerHTML={richText(block.text)} />
            </div>
          </article>
        );
      }
      if (block.type === 'group') {
        // The anchor keeps the group's old full-row width (this is what its items' width
        // math is computed against, via cqw — see .public-group-item) so under-filled rows
        // don't change how big each item renders. The inner .public-proof-block-group is the
        // actual visual card — shrink-wrapped to whatever width its items actually need, so
        // its background/border doesn't extend past them into empty space, and centered by
        // the anchor's own flex layout.
        return (
          <div key={index} className={`public-proof-block-group-anchor ${widthClass}`}>
            <div className="public-proof-block-group" style={getGroupContainerStyle(block)}>
              {renderGroup(block)}
            </div>
          </div>
        );
      }
      if (block.type === 'container') {
        return (
          <div key={index} className={`public-proof-block-container ${widthClass}`} style={getContainerStyle(block, 'none')}>
            {Array.isArray(block.children) && block.children.length > 0 && renderProofBlocks(block.children)}
          </div>
        );
      }
      const textStyle = {
        textAlign: block.align || 'left',
        lineHeight: block.spacing || undefined,
        background: block.bgColor || undefined,
        // border-box — this element gets width:100% from widthClass, so without it the
        // padding below would add on top of that 100% and overflow past the container.
        boxSizing: block.bgColor ? 'border-box' : undefined,
        padding: block.bgColor ? '14px 16px' : undefined,
        borderRadius: block.bgColor ? '10px' : undefined,
      };
      return block.to ? (
        <Link key={index} className={`public-proof-block-text ${widthClass}`} to={block.to} style={textStyle} dangerouslySetInnerHTML={richText(block.html)} />
      ) : (
        <p key={index} className={`public-proof-block-text ${widthClass}`} style={textStyle} dangerouslySetInnerHTML={richText(block.html)} />
      );
    })}
  </div>
);

// One nav item in the editor's edit-mode nav bar (see PublicSiteHeader's navEditor prop) —
// draggable left/right to reorder, with Remove revealed above on hover and Rename/Hide
// revealed below (centered together), instead of the old admin-only Navigation Links form.
const NavEditableItem = ({ item, index, isDragSource, isDragOver, onDragStart, onDragOver, onDrop, onDragEnd, navEditor }) => {
  const isProtected = navEditor.protectedTos?.includes(item.to);
  const isCurrentPage = navEditor.currentPage && getPageKeyFromPath(item.to) === navEditor.currentPage;
  return (
    <span
      className={`public-site-nav-edit-item${item.hidden ? ' public-site-nav-hidden' : ''}${isDragOver ? ' public-site-nav-drag-over' : ''}${isDragSource ? ' public-site-nav-drag-source' : ''}${isCurrentPage ? ' public-site-nav-edit-item-active' : ''}`}
      draggable
      onDragStart={() => onDragStart(index)}
      onDragOver={(e) => { e.preventDefault(); onDragOver(index); }}
      onDrop={(e) => { e.preventDefault(); onDrop(index); }}
      onDragEnd={onDragEnd}
    >
      <span className="public-site-nav-edit-row public-site-nav-edit-top">
        {isProtected
          ? <span className="public-site-nav-edit-btn public-site-nav-edit-btn-locked" title="Cannot remove this link">🔒</span>
          : <button type="button" className="public-site-nav-edit-btn" title="Remove from navigation" onClick={() => navEditor.onRemove(index)}>×</button>
        }
      </span>
      <button type="button" className="public-site-nav-edit-label" onClick={() => navEditor.onNavigate(getPageKeyFromPath(item.to))}>{item.label}</button>
      <span className="public-site-nav-edit-row public-site-nav-edit-bottom">
        <button type="button" className="public-site-nav-edit-btn" title="Rename" onClick={() => navEditor.onRename(index)}>✎</button>
        <button type="button" className="public-site-nav-edit-btn" title={item.hidden ? 'Show on public site' : 'Hide from public site'} onClick={() => navEditor.onToggleHidden(index)}>{item.hidden ? '🙈' : '👁'}</button>
      </span>
    </span>
  );
};

// Shared between the live site and the admin editor's "fullscreen" mode (see
// PublicSiteManager.js) — same exact header markup either way, so there's no separate
// implementation that can drift out of sync the way the section-rendering classes did.
// interactive=false renders nav/sign-in as plain spans instead of <Link>s, for the editor
// preview, where clicking them shouldn't navigate away from the page being edited.
// navEditor, when provided, replaces the plain nav links with the fullscreen editor's
// drag-to-reorder / Remove / Rename / Hide / Add nav bar — see NavEditableItem above. This is
// the only place nav items get managed now; there's no separate admin form for it anymore.
export const PublicSiteHeader = ({ branding, isSignedIn, highlightTarget = null, interactive = true, navEditor = null, storeName = null }) => {
  const allItems = normalizeBrandingNavItems(branding).map((item, index) => ({ item, index }));
  const navItems = navEditor ? allItems : allItems.filter(({ item }) => !item.hidden);
  const NavTag = navEditor ? 'button' : (interactive ? Link : 'span');
  const navProps = (to) => {
    if (navEditor) return { type: 'button', onClick: () => navEditor.onNavigate(getPageKeyFromPath(to)) };
    if (!interactive) return {};
    if (to === '/order') return { to, target: '_blank', rel: 'noopener noreferrer' };
    return { to };
  };
  // The "My Dashboard" / "Get Started" action isn't a public page, so it never switches the
  // editor's page even in edit mode — it stays a plain, non-interactive span there.
  const ActionTag = interactive && !navEditor ? Link : 'span';
  const actionProps = (to) => (interactive && !navEditor ? { to } : {});

  return (
    <header className="public-site-header">
      <div className="public-site-header-inner" style={branding?.wrapperEnabled === false ? { background: 'transparent' } : (branding?.wrapperColor ? { background: branding.wrapperColor } : undefined)}>
        <ServsoloLogo branding={branding} highlightTarget={highlightTarget} onClick={navEditor ? () => navEditor.onNavigate('home') : undefined} storeName={storeName} />
        <nav className={`public-site-nav${navEditor ? ' public-site-nav-editable' : ''}`}>
          {navEditor ? (
            <>
              {navItems.map(({ item, index }) => (
                <NavEditableItem
                  key={index}
                  item={item}
                  index={index}
                  isDragSource={navEditor.dragIndex === index}
                  isDragOver={navEditor.overIndex === index}
                  onDragStart={navEditor.onDragStart}
                  onDragOver={navEditor.onDragOver}
                  onDrop={navEditor.onDrop}
                  onDragEnd={navEditor.onDragEnd}
                  navEditor={navEditor}
                />
              ))}
              <button type="button" className="public-site-nav-add-btn" title="Add nav link" onClick={navEditor.onAdd}>+</button>
            </>
          ) : (
            navItems.map(({ item, index }) => (
              <NavTag key={`${item.to}-${index}`} {...navProps(item.to)} className={targetClass('', isTargetActive(highlightTarget, `branding.navItems.${index}`))} data-psm-target={`branding.navItems.${index}`}>{item.label}</NavTag>
            ))
          )}
        </nav>
        <div className="public-site-actions">
          {isSignedIn
            ? <ActionTag className="public-site-signup" {...actionProps('/dashboard')}>My Dashboard</ActionTag>
            : <ActionTag className="public-site-signup" {...actionProps('/login')}>Get Started</ActionTag>
          }
        </div>
      </div>
    </header>
  );
};

const CONTENT_CACHE_KEY = 'ss_public_site_content_v2';

const PublicSite = ({ page = 'home', isSignedIn = false, contentOverride = null, highlightTarget = null, storeName = null }) => {
  const [fetchedContentMap, setFetchedContentMap] = useState(() => {
    try {
      const cached = localStorage.getItem(CONTENT_CACHE_KEY);
      if (cached) return buildPublicSiteContentMap(JSON.parse(cached));
    } catch {}
    return buildPublicSiteContentMap();
  });

  useEffect(() => {
    if (contentOverride) return;
    let active = true;
    fetchPublicSiteContent()
      .then((rows) => {
        if (!active) return;
        try { localStorage.setItem(CONTENT_CACHE_KEY, JSON.stringify(rows)); } catch {}
        setFetchedContentMap(buildPublicSiteContentMap(rows));
      })
      .catch(() => {});

    return () => {
      active = false;
    };
  }, [contentOverride]);

  const contentMap = contentOverride || fetchedContentMap;

  const branding = contentMap.branding || PUBLIC_SITE_DEFAULTS.branding;
  const content = contentMap[page] || PUBLIC_SITE_DEFAULTS[page] || { customSections: [] };

  return (
    <div className={`public-site-shell${branding?.wrapperEnabled === false ? ' public-site-no-wrapper' : ''}`} style={branding?.backgroundColor ? { background: branding.backgroundColor } : undefined}>
      <PublicSiteHeader branding={branding} isSignedIn={isSignedIn} highlightTarget={highlightTarget} storeName={storeName} />

      <main className="public-site-content-wrapper" style={branding?.backgroundColor ? { background: branding.backgroundColor } : undefined}>
        {/* Every page (Home included) is a blank canvas built entirely from customSections —
            there's no fixed hero or per-page template anymore. A page with nothing added yet
            just shows the empty-state message below. */}
        <div className="public-home-sections-flow">
          {Array.isArray(content.customSections) && content.customSections.length > 0
            ? content.customSections.map((cs, csIndex) => (
              <section
                key={cs.id || csIndex}
                className="public-custom-section-band"
              >
                <div className="public-section public-custom-section-inner" style={{ ...getContainerStyle(cs, 'none'), background: getSectionBackground(cs, branding) }}>
                  {Array.isArray(cs.children) && cs.children.length > 0 && renderProofBlocks(cs.children)}
                </div>
              </section>
            ))
            : <div className="public-test-page-empty">This page is empty — add sections from the editor to see them here.</div>}
        </div>
      </main>
    </div>
  );
};

export default PublicSite;